import 'devextreme/dist/css/dx.light.css';
/* eslint-disable react/jsx-filename-extension */
import React, { useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import socketio from 'socket.io-client';
import {
  Route,
  Navigate,
  useLocation,
  BrowserRouter,
  Routes,
} from 'react-router-dom';
import socketEvents from 'utils/sockets';
import { getRoutes as _getRoutes, authRoutes as _authRoutes } from 'routes/index';
import { getModelPermission } from 'utils/permission';
import { withFullPaths } from 'utils/sidebarMenuConfig';
import { model as addressModel } from 'components/Direccion';
import NotFound from 'components/NotFound';
import Layout from 'components/Layout';
import {
  selectisAuthenticated,
  selectPeriodoFiscalSelected,
  selectToken,
  seelectEntidad,
} from 'store/auth';
import { setFavicon } from 'utils/favicon';
import Blank from 'components/Blank';
import { getUserInfo } from 'api/usuarios/auth';
import { selectEstadosGlobales } from 'store/catalogos';
import { getEstadosGlobales } from 'api/catalogos';
import AuthLayout from 'views/Auth/components/AuthLayout';
import { SOCKETS_URL } from 'utils/env';
import config from 'devextreme/core/config';
import { licenseKey } from './devextreme-license';

config({ licenseKey });

// El grupo "Presupuestos de Nómina" se renombró a "Presupuesto de Nómina" (la URL sale del título):
// las ligas guardadas con la ruta anterior se redirigen a la nueva.
const RedirectPresupuestoDeNomina = () => {
  const { pathname, search } = useLocation();
  const destino = pathname.replace(/^\/presupuestos-de-nomina/, '/presupuesto-de-nomina');
  return <Navigate replace to={`${destino}${search}`} />;
};

const AppRouter = () => {
  const dispatch = useDispatch();
  const token = useSelector(selectToken);
  const periodoFiscalSelected = useSelector(selectPeriodoFiscalSelected);
  const estadosGlobales = useSelector(selectEstadosGlobales);
  const isAuthenticated = useSelector(selectisAuthenticated);
  const logotipoEntidad = useSelector(seelectEntidad)?.logotipo;
  const io = useRef();

  // Icono de la pestaña = logotipo de la Entidad (si no tiene, el de por defecto).
  // Sin sesión lo asigna la pantalla de inicio de sesión.
  useEffect(() => {
    if (isAuthenticated) {
      setFavicon(logotipoEntidad);
    }
  }, [isAuthenticated, logotipoEntidad]);

  useEffect(() => {
    if (token && SOCKETS_URL) {
      if (!io.current) {
        io.current = socketio(SOCKETS_URL, { auth: { token } });
      } else {
        io.current.auth = { token };
      }
    } else {
      io.current = null;
    }
    socketEvents(io.current);
  }, [token]);

  useEffect(() => {
    if (token) {
      getUserInfo(dispatch);
      getEstadosGlobales(dispatch, estadosGlobales);
    }
  }, []);

  // El catálogo de estados se carga también al iniciar sesión (el efecto anterior solo corre al
  // montar, cuando aún no hay token), para que los grids muestren la descripción del estado.
  useEffect(() => {
    if (token && !estadosGlobales.length) {
      getEstadosGlobales(dispatch, estadosGlobales);
    }
  }, [token]);

  const [routesWithLayout, routesWithoutLayout] = useMemo(() => {
    const _routes = _getRoutes();
    // Árbol completo con fullPath congelado por nodo — se pasa como prop a cada vista
    // (igual que `permission`) para que quien lo necesite (ej. PermisosSidebar) no tenga
    // que importar routes/index directamente y crear un ciclo con routes/configuracion.
    const fullRoutesTree = withFullPaths(_routes);
    const getRoutes = (_r, parentPath = '') => isAuthenticated && _r.map((r) => {
      if (r.children) {
        return getRoutes(r.children, parentPath + r.path);
      }
      const addressPermission = getModelPermission(addressModel);
      const permission = getModelPermission(r.permissionModel, r.catalogsModels, r.tabsModels);
      const Component = r.component || Blank;
      return (
        <Route
          exact
          key={parentPath + r.path}
          path={parentPath + r.path}
          element={(
            <Component
              permission={permission}
              addressPermission={addressPermission}
              routesTree={fullRoutesTree}
            />
          )}
        />
      );
    });
    const allowedRoutes = periodoFiscalSelected?.vigente
      ? _routes : _routes.filter((e, i) => i !== 2);
    return [
      getRoutes(allowedRoutes.filter((e) => !e.withoutLayout)),
      getRoutes(allowedRoutes.filter((e) => e.withoutLayout)),
    ];
  }, [periodoFiscalSelected]);

  const mapAuthRoutes = (route) => (
    <Route
      exact
      key={route.path}
      path={route.path}
      element={<route.component />}
    />
  );

  const authRoutesWithLayout = _authRoutes.filter((e) => !e.withoutLayout).map(mapAuthRoutes);
  const authRoutesWithoutLayout = _authRoutes.filter((e) => e.withoutLayout).map(mapAuthRoutes);

  return (
    <div className="app-container">
      <BrowserRouter>
        <Routes>
          <Route
            exact
            path="/"
            element={<Navigate to={isAuthenticated ? '/empleados/empleados' : '/inicio-de-sesion'} />}
          />
          <Route
            exact
            path="/catalogos/operativos/otros/documentos-para-liquidaciones"
            element={<Navigate replace to="/catalogos/operativos/otros/documentos-para-anexar" />}
          />
          {isAuthenticated ? routesWithoutLayout : authRoutesWithoutLayout}
          <Route element={isAuthenticated ? <Layout /> : <AuthLayout />}>
            {isAuthenticated ? routesWithLayout : authRoutesWithLayout}
          </Route>
          <Route
            path="/presupuestos-de-nomina/*"
            element={<RedirectPresupuestoDeNomina />}
          />
          <Route
            path="*"
            element={isAuthenticated ? <NotFound /> : <Navigate to="/inicio-de-sesion" />}
          />
        </Routes>
      </BrowserRouter>
    </div>
  );
};

export default AppRouter;
